import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DevImpersonationService } from './dev-impersonation.service';
import { FEATURE_FLAG_CONFIGS } from '../_config/feature-flag.constants';
import { isFeatureFlagHostAllowed } from '../_config/feature-flag-host';
import { getPortalViewFromAttributes } from '../_config/portal-view';
import { ConfigService } from '../config/config.service';
import * as i0 from "@angular/core";
import * as i1 from "./dev-impersonation.service";
import * as i2 from "../config/config.service";
var FeatureFlagService = /** @class */ (function () {
    function FeatureFlagService(devImpersonationService, configService) {
        var _this = this;
        this.devImpersonationService = devImpersonationService;
        this.configService = configService;
        this.runtimeConfigs = {};
        // Subscribe to impersonation token changes to invalidate cache
        this.devImpersonationService.token$.subscribe(function () {
            // Clear runtime configs when impersonation changes (they may be user-specific)
            _this.runtimeConfigs = {};
        });
    }
    /**
     * Get config for a feature (from runtime override or constants)
     * @param featureName The name of the feature (e.g., 'orders')
     * @returns FeatureFlagConfig or null if not found
     */
    FeatureFlagService.prototype.getConfig = function (featureName) {
        var runtimeConfig = this.runtimeConfigs[featureName];
        if (runtimeConfig) {
            return runtimeConfig;
        }
        return FEATURE_FLAG_CONFIGS[featureName] || null;
    };
    /**
     * Set allowed roles for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param roles Array of allowed roles
     */
    FeatureFlagService.prototype.setAllowedRolesForFeature = function (featureName, roles) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].roles = roles || [];
    };
    /**
     * Set allowed emails for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param emails Array of allowed emails
     */
    FeatureFlagService.prototype.setAllowedEmailsForFeature = function (featureName, emails) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].emails = emails || [];
    };
    /**
     * Set allowed client IDs for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param ids Array of allowed client IDs
     */
    FeatureFlagService.prototype.setAllowedClientIdsForFeature = function (featureName, ids) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].clientIds = ids || [];
    };
    /**
     * Set allowed market IDs for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param ids Array of allowed market IDs
     */
    FeatureFlagService.prototype.setAllowedMarketIdsForFeature = function (featureName, ids) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].marketIds = ids || [];
    };
    /**
     * Set allowed campus IDs for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param ids Array of allowed campus IDs
     */
    FeatureFlagService.prototype.setAllowedCampusIdsForFeature = function (featureName, ids) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].campusIds = ids || [];
    };
    /**
     * Set allowed distributor IDs for a feature at runtime (e.g., from API)
     * This overrides the constants file configuration
     * @param featureName The name of the feature (e.g., 'orders')
     * @param ids Array of allowed distributor IDs
     */
    FeatureFlagService.prototype.setAllowedDistributorIdsForFeature = function (featureName, ids) {
        if (!this.runtimeConfigs[featureName]) {
            this.runtimeConfigs[featureName] = {};
        }
        this.runtimeConfigs[featureName].distributorIds = ids || [];
    };
    /**
     * Primary generalized method to check if user has access to a feature flag
     * Host allowlist is a pre-filter when configured.
     * If an email allowlist is present, email must match first (only for emailRoles, when those are set).
     * Then checks in priority order: roles -> portalViews -> clientIds -> marketIds -> campusIds -> distributorIds
     * @param featureName The name of the feature (e.g., 'orders')
     * @returns true if user has access, false otherwise
     */
    FeatureFlagService.prototype.hasFeatureFlag = function (featureName) {
        var config = this.getConfig(featureName);
        if (!config) {
            return false;
        }
        if (!isFeatureFlagHostAllowed(config.hosts, this.getCurrentHost())) {
            return false;
        }
        var userAttr = this.devImpersonationService.getUserAttributes();
        if (!userAttr) {
            return false;
        }
        var userRole = userAttr['custom:role'];
        var emailAttr = userAttr['email'] || userAttr['custom:email'];
        var email = emailAttr ? String(emailAttr).toLowerCase() : null;
        var clientId = userAttr['custom:client_id'];
        var marketId = userAttr['custom:market_id'];
        var campusId = userAttr['custom:campus_id'];
        var distiId = userAttr['custom:disti_id'];
        // Required pre-filter: if emails are configured, user email must be allowed
        var emailsApply = !config.emailRoles || config.emailRoles.length === 0 || config.emailRoles.indexOf(userRole) !== -1;
        if (config.emails && config.emails.length > 0 && emailsApply) {
            if (!email) {
                return false;
            }
            var normalizedAllowedEmails = config.emails.map(function (allowedEmail) { return String(allowedEmail).toLowerCase(); });
            if (normalizedAllowedEmails.indexOf(email) === -1) {
                return false;
            }
        }
        // Priority 1: Check allowed roles
        if (config.roles && config.roles.length > 0 && userRole) {
            if (config.roles.includes(userRole)) {
                return true;
            }
        }
        // Priority 1.5: Check allowed portal views
        if (config.portalViews && config.portalViews.length > 0) {
            if (config.portalViews.includes(getPortalViewFromAttributes(userAttr))) {
                return true;
            }
        }
        // Priority 2: Check allowed client IDs
        if (config.clientIds && config.clientIds.length > 0 && clientId) {
            if (config.clientIds.includes(clientId)) {
                return true;
            }
        }
        // Priority 2.5: Check allowed market IDs
        if (config.marketIds && config.marketIds.length > 0 && marketId) {
            if (config.marketIds.includes(marketId)) {
                return true;
            }
        }
        // Priority 3: Check allowed campus IDs
        if (config.campusIds && config.campusIds.length > 0 && campusId) {
            if (config.campusIds.includes(campusId)) {
                return true;
            }
        }
        // Priority 4: Check allowed distributor IDs
        if (config.distributorIds && config.distributorIds.length > 0 && distiId) {
            if (config.distributorIds.includes(distiId)) {
                return true;
            }
        }
        // Default: deny access
        return false;
    };
    /**
     * Convenience method to check orders access
     * @returns true if user has access to orders, false otherwise
     */
    FeatureFlagService.prototype.canAccessOrders = function () {
        return this.hasFeatureFlag('orders');
    };
    FeatureFlagService.prototype.canAccessVouchers = function () {
        return this.hasFeatureFlag('vouchers');
    };
    FeatureFlagService.prototype.canAccessFreeMode = function () {
        return this.hasFeatureFlag('free_mode');
    };
    /**
     * Reactive check for orders access
     * Observes DevImpersonationService.token$ for changes
     * @returns Observable<boolean> that emits true if user has access to orders
     */
    FeatureFlagService.prototype.canAccessOrders$ = function () {
        var _this = this;
        return combineLatest([
            this.devImpersonationService.token$
        ]).pipe(map(function () { return _this.canAccessOrders(); }));
    };
    FeatureFlagService.prototype.getCurrentHost = function () {
        if (!this.configService.isLoaded()) {
            return '';
        }
        return String(this.configService.get('host') || '');
    };
    FeatureFlagService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function FeatureFlagService_Factory() { return new FeatureFlagService(i0.ɵɵinject(i1.DevImpersonationService), i0.ɵɵinject(i2.ConfigService)); }, token: FeatureFlagService, providedIn: "root" });
    return FeatureFlagService;
}());
export { FeatureFlagService };
